import { notification } from "antd";
import { API_BASE_URL } from "../../shared/constants";
const getAccessToken = (): string | null => {
  const token = localStorage.getItem("accessToken")?.trim();
  if (token) return token;

  try {
    const persistedRoot = localStorage.getItem("persist:root");
    if (!persistedRoot) return null;

    const root = JSON.parse(persistedRoot);
    const auth =
      typeof root.auth === "string" ? JSON.parse(root.auth) : root.auth;
    const persistedToken = auth?.accessToken;

    if (typeof persistedToken === "string" && persistedToken.trim()) {
      localStorage.setItem("accessToken", persistedToken);
      return persistedToken;
    }
  } catch {
    // Ignore invalid persisted state.
  }

  return null;
};
//MOCK_API_BASE_URL as
const displayError = async (data: any, shouldHide: any) => {
  let message = "Something went wrong";
  if (data?.errors?.errorDetails?.length > 0) {
    const error = data?.errors?.errorDetails[0];
    message = error.message;
  }
  if (data?.message) {
    message = data.message;
  }
  !shouldHide &&
    notification.error({
      message: message,
    });
};

export async function Client(
  endpoint: string,
  { method, body, ...customConfig } = {} as any
) {
  const headers: any = {
    "Content-Type": "application/json",
  };
  const accessToken = getAccessToken();

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }
  if (body instanceof FormData) {
    delete headers["Content-Type"]; // Let the browser set the correct boundary
  }
  const config = {
    method: method,
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
    redirect: "follow",
  };
  if (body) {
    if (body instanceof FormData) {
      config.body = body; // ✅ Correct handling
    }
    else
      if (config.headers["Content-Type"] !== "application/json") {
        config.body = new URLSearchParams(Object.entries(body)).toString();
      } else {
        config.body = JSON.stringify(body);
      }
  }
  let data;
  try {
    const response = await window.fetch(API_BASE_URL + endpoint, config);
    return processResponse(response, customConfig);
  } catch (err: any) {
    displayError(
      { message: "Something went wrong", code: "ERR001" },
      customConfig?.shouldHide
    );
    return Promise.reject(err.message ? err.message : data);
  }
}

// Fetch `Headers` instances are not serializable, and these responses are stored
// in Redux. Flatten them to a plain object so redux-toolkit's serializability
// check stays quiet and the store holds only plain data.
const toPlainHeaders = (headers: Headers) => {
  const plainHeaders: Record<string, string> = {};
  headers?.forEach?.((value: string, key: string) => {
    plainHeaders[key] = value;
  });
  return plainHeaders;
};

const processResponse = async (response: any, customConfig: any) => {
  let data = await response.json();
  if (response.ok) {
    if (response.status === 204) {
      return {
        status: response.status,
        headers: toPlainHeaders(response.headers),
        url: response.url,
      };
    }
    return {
      status: response.status,
      data,
      headers: toPlainHeaders(response.headers),
      url: response.url,
    };
  } else if (response.status === 401) {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
    // Never settle: a rejected thunk would change the auth slice and redux-persist
    // would save the dead token again before the reload, causing a reload loop.
    return new Promise<never>(() => {});
  }
  else if (response.status === 400) {
    return {
      status: response.status,
      data,
      headers: toPlainHeaders(response.headers),
      url: response.url,
    };
  }
  displayError(data, customConfig?.shouldHide);
  return Promise.reject(data);
};

Client.get = function (endpoint: string, customConfig: any = {}) {
  if (customConfig.params) {
    const params = createQS(customConfig);
    return Client(endpoint + "?" + params.join("&"), {
      ...customConfig,
      method: "GET",
    });
  } else {
    delete customConfig.params;
    return Client(endpoint, { ...customConfig, method: "GET" });
  }
};
Client.post = function (endpoint: string, body: any, customConfig: any = {}) {
  if (customConfig.params) {
    const params = createQS(customConfig);
    endpoint = endpoint + "?" + params.join("&");
  }
  delete customConfig.params;
  return Client(endpoint, { ...customConfig, body, method: "POST" });
};
Client.put = function (endpoint: string, body: any, customConfig = {}) {
  return Client(endpoint, { ...customConfig, body, method: "PUT" });
};
Client.patch = function (endpoint: string, body: any, customConfig = {}) {
  return Client(endpoint, { ...customConfig, body, method: "PATCH" });
};
Client.delete = function (endpoint: string, customConfig = {}) {
  return Client(endpoint, { ...customConfig, method: "DELETE" });
};
const createQS = (customConfig: any) => {
  let params: any = [];
  for (let p in customConfig.params) {
    let key = p;
    if (Array.isArray(customConfig.params[p])) {
      customConfig.params[p].forEach((element: any) => {
        params.push(`${key}=${encodeURIComponent(element)}`);
      });
    } else {
      params.push(`${key}=${encodeURIComponent(customConfig.params[p])}`);
    }
  }
  return params;
};
